import { useState, useEffect, useRef } from "react";

import { useStepContext } from "../../utils/StepContext.jsx"
import { useFormContext } from "../../utils/FormContext.jsx"
import { useMediaQuery } from 'react-responsive';
import { useAuth } from "../../utils/AuthContext/AuthContext.jsx"; 

import MainCard from "../MainCard.jsx";
import ProgressBar from "../ProgressBar/ProgressBar.jsx";
import InfoModal from "../InfoModal.jsx";

import InfoSVG from "../../assets/InfoSVG.jsx";
import SaveButton from "../SaveButton.jsx"; 
import PDFPreview from "../PDFPreview.jsx";
import HelpButton from "../HelpButton.jsx";
import WarningSVG from "../../assets/Warning.jsx";
import ShishitoAssist from "../ShishitoAssist/ShishitoAssist.jsx";

const Miscellaneous = () => {

    const currentPageValue = 0;
    const currentSubStepProgress = 5/5; 

    const { handleNext, handleBack, markStepAsComplete, unsetStepAsComplete } = useStepContext();
    const { token, user } = useAuth();
    const [isAssistVisible, setIsAssistVisible] = useState(false);
    const { formData, handleAttachedInclusions, handleAttachedInclusionsChange, handleOtherInclusions, handleEncumberedInclusions, handleDisclosureChange, handleInclusionsTextChange, handleAddtlWaterChange, handleWaterRightsChange, handleWaterRightsReviewChange, handleWaterRightsTextChange, setFormData, formId } = useFormContext(); 

    const [isModalOpen, setIsModalOpen] = useState(false);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    

    const handleModalOpen = () => {
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
    };

    const onBack = () => {
        unsetStepAsComplete(currentPageValue);
        handleBack();
    };

    const onNext = () => {
            markStepAsComplete(currentPageValue); // Mark this step as complete
            handleNext(); // Navigate to the next step
    };

    const toggleAssist = () => {
        setIsAssistVisible(!isAssistVisible);
    };

    const inclusionValues = formData?.inclusionsPreStep?.encumberedInclusionsValues !== '' 
    ? formData.inclusionsPreStep.encumberedInclusionsValues 
    : "Other Values";

    const leasedValues = formData?.inclusionsPreStep?.leasedItemsValues !== '' 
    ? formData.inclusionsPreStep.leasedItemsValues 
    : "Other Values";

    const hasEncumberedInclusions = () => {
        const { solarPanels, waterSofteners, satelliteSystems, securitySystems, other } = formData.inclusionsPreStep;
        const isIncluded = item => item.included;
        const isEncumbered = item => ['financed', 'unsure', null].includes(item.status);
    
        return [solarPanels, waterSofteners, satelliteSystems, securitySystems, other].some(item => isIncluded(item) && isEncumbered(item));
    };

    const hasLeasedItem = () => {
        const { solarPanels, waterSofteners, satelliteSystems, securitySystems, other } = formData.inclusionsPreStep;
        const isIncluded = item => item.included;
        const isEncumbered = item => ['leased', 'unsure', null].includes(item.status);
    
        return [solarPanels, waterSofteners, satelliteSystems, securitySystems, other].some(item => isIncluded(item) && isEncumbered(item));
    };

    const hasWater = () => {
        const { waterRights } = formData.inclusionsPreStep;
        const isIncluded = item => item.included;
    
        return [waterRights].some(item => isIncluded(item));
    };

    const hasWell = () => {
        const { wellRights } = formData.inclusionsPreStep;
        const isIncluded = item => item.included;
    
        return [wellRights].some(item => isIncluded(item));
    };

    return (
        <div className="w-full flex flex-col h-screen bg-shi-eggshell items-center">
        {/* <ProgressBar currentPage={currentPageValue} subStepProgress={currentSubStepProgress}/> */}
        <MainCard width={750} maxHeight={450} gap={0}>
        <div className="overflow-y-auto md:max-h-[425px] max-h-[45vh] w-full">
            <div className="flex flex-col">
                <div className='inline-flex justify-between items-center w-full mb-5'>
                    {isMobile ? <h3 className="text-black text-[18px] text-left font-normal font-['Inter']">Inclusions</h3> : <h3 className="text-black text-xl text-left font-normal font-['Inter']">Inclusions</h3>}
                    <div className='gap-2 md:gap-4 items-center h-auto inline-flex'>
                        <HelpButton onClick={toggleAssist}/>
                        <SaveButton formData={formData} formId={formId}/>
                        {user && <PDFPreview formData={formData}/>}
                    </div>
                </div>
                <div className="border-blue-500 bg-blue-50 border rounded-md md:p-2 p-1 inline-flex justify-between w-full gap-2 mb-4 items-center">
                    {isMobile ? <InfoSVG width={18} height={18} fill='#2563EB'/> : <InfoSVG width={23} height={23} fill='#2563EB'/>}
                    <span className="text-blue-800 text-[13px] w-full"><b>IMPORTANT:</b> Verify all information with selling agent, especially encumbered inclusions & leased items.</span>
                </div>
                
            </div>
        <div className="flex flex-col gap-2 w-full text-left">
        <div className="inline-flex justify-between">
            <div className="inline-flex justify-between w-full">
                <div className="flex flex-col">
                    {/* OTHER INCLUSIONS */}
                    <div className="relative flex flex-col mb-6 w-full border rounded-md border-slate-400 p-1.5">
                            <span className="absolute -top-2 left-2 bg-white px-1 text-[12px] md:text-sm font-[Inter] text-slate-700">OTHER INCLUSIONS</span>
                            <span className="text-[12px] md:text-sm font-[Inter] text-slate-500 mb-2 mt-4 pl-2">Major items affixed to the property that are included in the sale and free of debt or ongoing leases. e.g. appliances, window treatments, home theater.</span>
                            <div className="self-stretch px-2 py-2 justify-start gap-[10px] flex-col items-center w-full">
                            <div className="flex flex-col justify-start mb-2">
                                <div className="items-center flex">                        
                                    <div className="flex flex-col gap-2 w-full">
                                        <span className="w-full text-slate-600 text-sm">Add any additional inclusions agreed with seller:</span>
                                        <textarea 
                                            name="otherInclusionsText"
                                            className="w-full bg-slate-100 p-1.5" 
                                            onChange={handleWaterRightsTextChange}
                                            value={formData.otherInclusionsText}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* ENCUMBERED INCLUSIONS */}
                    {hasEncumberedInclusions() && (
                    <div className={`relative flex flex-col mb-6 w-full border rounded-md ${hasEncumberedInclusions() ? 'border-yellow-500 border-[3px]' : 'border-slate-400'} p-1.5`}>
                    <div className="inline-flex">
                    <span className={`absolute -top-2 left-2 bg-white px-2 text-[12px] md:text-sm font-[Inter] flex items-center ${hasEncumberedInclusions() ? 'text-yellow-700 font-bold' : 'text-slate-700'}`}>
                      <WarningSVG fill="#b45309" className="mr-1"/> ENCUMBERED INCLUSIONS
                    </span>
                    </div>
                    <span className="text-[12px] md:text-sm font-[Inter] text-black font-semibold mb-2 mt-4 pl-2">An encumbered inclusion is a claim against an asset by an entity that is not the owner. Encumbered Inclusions come with financial obligations, you should check with the seller how the ongoing debt obligation will be managed.</span>
                    <span className="text-[12px] md:text-sm font-[Inter] text-slate-500 mb-2 pl-2"></span>
                        <div className="self-stretch px-2 py-2 justify-start gap-[10px] flex-col items-center w-full">
                            <div className="flex flex-col justify-start mb-2">
                                <div className="items-center flex">
                                        <div className="flex flex-col gap-2 w-full">
                                            <span className="text-slate-600 text-sm">
                                                You chose <span> </span>
                                                <div className="w-auto border border-blue-500 rounded-lg p-[1px] bg-blue-50 font-semibold inline-block">
                                                    { inclusionValues }
                                                </div> 
                                                <span> </span> as included in the property and potentially not paid off. We will add these values to the contract, add any additional encumbered inclusions if you know of any here:
                                            </span>
                                            <textarea 
                                                name="encumberedInclusionsManualText"
                                                className="w-full bg-slate-100 p-1.5" 
                                                onChange={handleInclusionsTextChange}
                                                value={formData.inclusionsPreStep.encumberedInclusionsManualText}
                                            />
                                        </div>
                                </div>
                            </div>
                        </div>
                        {/* CHECKBOX SECTION */}
                        <div className="flex flex-col w-full mb-2 ml-2">
                            <span className='text-[14px] md:text-[16px] text-slate-600 font-[Inter]'>Encumbered Inclusion Responsibility</span>
                            {/* <span className="w-full md:w-[500px] h-auto text-shi-purple mb-2 text-xs font-['Inter']">Encumberances are liens, Easements, Deeds, etc. Will you the buyer assume the debt on these Encumberances. Most likely NO.</span> */}
                            <span className="w-[full h-auto text-black mb-2 text-[13px] md:text-xs font-['Inter']">Will you assume the debt and obligation on the property above? Typically No.</span>
                            <div className="self-stretch justify-start gap-[25px] inline-flex items-center">
                                <div className="items-center gap-2 flex">
                                    <input 
                                        type="radio"
                                        id="encumberedYes" 
                                        value="yes" 
                                        checked={formData.encumberedInclusions === 'yes'} 
                                        onChange={(e) => handleDisclosureChange('encumberedInclusions', e.target.value)}
                                        
                                    />
                                    <label className="text-black text-opacity-60 font-semibold font-['Inter']">Yes</label>
                                </div>
                                <div className="items-center gap-2 flex">
                                    <input 
                                        type="radio" 
                                        id="encumberedNo"
                                        value="no" 
                                        checked={formData.encumberedInclusions === 'no'} 
                                        onChange={(e) => handleDisclosureChange('encumberedInclusions', e.target.value)}
                                    />
                                    <label className="text-black text-opacity-60 font-semibold font-['Inter']">No</label>
                                </div>
                            </div>
                        </div>  
                    </div>
                    )}
                    {/* LEASED ITEMS */}
                    {hasLeasedItem() && (
                    <div className={`relative flex flex-col mb-6 w-full border rounded-md ${hasLeasedItem() ? 'border-yellow-500 border-[3px]' : 'border-slate-400'} p-1.5`}>
                    <div className="inline-flex">
                    <span className={`absolute -top-2 left-2 bg-white px-1 text-[12px] md:text-sm font-[Inter] flex items-center ${hasLeasedItem() ? 'text-yellow-700 font-bold' : 'text-slate-700'}`}>
                        <WarningSVG fill="#b45309" className="mr-1"/> LEASED ITEMS 
                    </span>
                    </div>
                    <span className="text-[12px] md:text-sm font-[Inter] text-black font-semibold mb-2 mt-4 pl-2">Leased Items come with financial obligations, you should check with the seller how the ongoing lease will be managed.</span>
                        <div className="self-stretch py-2 justify-start gap-[10px] flex-col md:mt-2 items-center w-full">
                            <div className="flex flex-col justify-start md:mb-2">
                                <div className="items-center flex">
                                    <div className="w-full self-stretch justify-between gap-[10px] inline-flex items-center">
                                        <div className="flex flex-col gap-2 w-full">
                                        <span className="text-slate-600 text-sm">
                                            You chose <span> </span>
                                            <div className="w-auto border border-blue-500 rounded-lg p-[1px] bg-blue-50 font-semibold inline-block">
                                                { leasedValues }
                                            </div> 
                                            <span> </span> as included in the property and potentially leased. We will add these values to the contract, add any additional leased items if you know of any here:
                                        </span>
                                            <textarea 
                                                name="leasedItemsManualText"
                                                className="w-full bg-slate-100 p-1.5" 
                                                onChange={handleInclusionsTextChange}
                                                value={formData.inclusionsPreStep.leasedItemsManualText}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* CHECKBOX SECTION */}
                        <div className="flex flex-col w-full mb-2 ml-2">
                            <span className='text-[14px] md:text-[16px] text-slate-600 font-[Inter]'>Leased Items Responsibility</span>
                            <span className="w-[full h-auto text-black mb-2 text-[13px] md:text-xs font-['Inter']">Will you assume the debt and obligation on the leased property above? Typically No.</span>
                            <div className="self-stretch justify-start gap-[25px] inline-flex items-center">
                            <div className="items-center gap-2 flex">
                                <input 
                                    type="radio"
                                    id="yesLease" 
                                    value="yes" 
                                    checked={formData.leasedItems === 'yes'} 
                                    onChange={(e) => handleDisclosureChange('leasedItems', e.target.value)}
                                    
                                />
                                <label className="text-black text-opacity-60 font-semibold font-['Inter']">Yes</label>
                            </div>
                            <div className="items-center gap-2 flex">
                                <input 
                                    type="radio" 
                                    id="noLease"
                                    value="no" 
                                    checked={formData.leasedItems === 'no'} 
                                    onChange={(e) => handleDisclosureChange('leasedItems', e.target.value)}
                                />
                                <label className="text-black text-opacity-60 font-semibold font-['Inter']">No</label>
                            </div>
                            </div>
                        </div>
                    </div>
                    )}
                    {/* EXCLUSIONS */}
                    <div className="relative flex flex-col mb-6 w-full border rounded-md border-slate-400 p-1.5">
                        <span className="absolute -top-2 left-2 bg-white px-1 text-[12px] md:text-sm font-[Inter] text-slate-700">EXCLUSIONS</span>
                        <span className="text-[12px] md:text-sm font-[Inter] text-slate-500 mb-2 mt-4 pl-2">Anything that is "excluded" from the purchase of this property. The seller may want to exclude certain items like "personal property" in the contract.</span>
                        <div className="self-stretch py-2 justify-start gap-[10px] flex-col mt-2 items-center w-full">
                            <div className="flex flex-col justify-start mb-2">
                                <div className="items-center flex">
                                    <div className="w-full self-stretch justify-between gap-[10px] inline-flex items-center">
                                        <div className="flex flex-col gap-2 w-full">
                                            <textarea 
                                                name="exclusionsText"
                                                className="w-full bg-slate-100 p-1.5" 
                                                onChange={handleWaterRightsTextChange}
                                                value={formData.exclusionsText}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* PARKING AND STORAGE */}
                    <div className="relative flex flex-col mb-6 w-full border rounded-md border-slate-400 p-1.5">
                        <span className="absolute -top-2 left-2 bg-white px-1 text-[12px] md:text-sm font-[Inter] text-slate-700">PARKING and/or STORAGE</span>
                        <span className="text-[12px] md:text-sm font-[Inter] text-slate-500 mb-2 mt-4 pl-2">This is any parking facility attached or unattached to the property or none if there is no parking facility.</span>
                        <div className="self-stretch py-2 justify-start gap-[10px] flex-col md:mt-2 items-center w-full">
                            <div className="flex flex-col justify-start md:mb-2">
                                <div className="items-center flex">
                                    <div className="w-full self-stretch justify-between gap-[10px] inline-flex items-center">
                                        <div className="flex flex-col gap-2 w-full">
                                            <span className="w-full text-slate-600 text-sm">PARKING: Default value pulled from property detail. Adjust as needed</span>
                                            <input 
                                                name="parkingFacility"
                                                type="text"
                                                className="w-full bg-slate-100 p-1.5" 
                                                onChange={handleWaterRightsTextChange}
                                                value={formData?.parkingFacility}
                                            />
                                            <span className="w-full text-slate-600 text-sm mt-2">STORAGE: Add any storage facilities included with property.</span>
                                            <input 
                                                name="storageFacility"
                                                type="text"
                                                className="w-full bg-slate-100 p-1.5" 
                                                onChange={handleWaterRightsTextChange}
                                                value={formData.storageFacility}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* WATER RIGHTS */}
                    {hasWater() && (
                    <div className={`relative flex flex-col mb-6 w-full border rounded-md ${hasWater() ? 'border-red-500 border-[3px]' : 'border-slate-400'} p-1.5`}>
                        <div className="inline-flex">
                        <span className={`absolute -top-2 left-2 bg-white px-1 text-[12px] md:text-sm font-[Inter] flex items-center ${hasWater() ? 'text-red-700 font-bold' : 'text-slate-700'}`}>
                        <WarningSVG fill="#ef4444" className="mr-1"/> WATER RIGHTS 
                        </span>
                        </div>
                        <span className="text-[13px] md:text-sm font-[Inter] text-black my-6 pl-2">You selected water rights in the previous step. Proceed cautiously and consult with a real estate attorney as needed.</span>
                        <div className="flex flex-col justify-start mb-6">
                            <div className="items-center flex">
                            <div className="w-full justify-between inline-flex items-center">
                                <div className="inline-flex gap-2 w-full">
                                    <input 
                                        type="checkbox" 
                                        id="deededWaterRights"
                                        value={formData.deededWaterRights} 
                                        checked={formData.deededWaterRights === 'yes'}
                                        onChange={(e) => handleWaterRightsChange('deededWaterRights', e.target.checked ? 'yes' : 'no')}
                                    />
                                    <label className="text-[14px] md:text-sm font-[Inter] text-slate-600">Deeded Water Rights</label>
                                </div>
                                {/* <InfoSVG onClick={handleModalOpen}/> */}
                            </div>
                            </div>
                                <span className="w-full text-slate-600 text-sm mt-2">Add legally described water rights below:</span>
                                <textarea 
                                    name="deededWaterRightsDescription"
                                    className="w-full bg-slate-100 p-1.5" 
                                    onChange={handleWaterRightsTextChange}
                                    value={formData.deededWaterRightsDescription}
                                />
                                <span className="text-black font-[Inter] text-[14px] md:text-[16px] w-full mt-2">Water Rights will be conveyed by a <input type="text" onChange={handleWaterRightsTextChange} value={formData.typeOfDeed} name="typeOfDeed" className="mt-2 h-auto rounded-sm border border-black w-[100px]"/> deed at closing</span>
                        </div>

                        <div className="flex flex-col justify-start mb-6">
                            <div className="items-center gap-2 mb-2 flex">
                                <input 
                                    type="checkbox" 
                                    id="otherWaterRights"
                                    value={formData.otherWaterRights} 
                                    checked={formData.otherWaterRights === 'yes'}
                                    onChange={(e) => handleWaterRightsChange('otherWaterRights', e.target.checked ? 'yes' : 'no')}
                                />
                                <label className="text-[14px] md:text-sm font-[Inter] text-slate-600">Other Rights Relating to Water</label>
                            </div>
                                <span className="w-full text-slate-600 text-sm">List other rights you believe should be transferred to buyer</span>
                                <textarea 
                                    name="otherWaterRightsDescription"
                                    onChange={handleWaterRightsTextChange}
                                    value={formData.otherWaterRightsDescription}
                                    className="w-full bg-slate-100 p-1.5" 

                                />
                        </div>
                    </div>
                    )}
                    {/* WELL RIGHTS */}
                    {hasWell() && (
                    <div className={`relative flex flex-col mb-6 w-full border rounded-md ${hasWell() ? 'border-red-500 border-[3px]' : 'border-slate-400'} p-1.5`}>
                    <div className="inline-flex">
                    <span className={`absolute -top-2 left-2 bg-white px-1 text-[12px] md:text-sm font-[Inter] flex items-center ${hasWell() ? 'text-red-700 font-bold' : 'text-slate-700'}`}>
                    <WarningSVG fill="#ef4444" className="mr-1"/> WELL RIGHTS 
                    </span>
                    </div>
                        <span className="text-[13px] md:text-sm font-[Inter] text-black my-6 pl-2">You selected well rights in the previous step. Proceed cautiously and consult with a real estate attorney as needed.</span>
                        <div className="flex flex-col justify-start mb-6">
                        <div className="w-full flex flex-col flex-wrap items-center">
                            <h3 className="text-[14px] md:text-sm w-full text-slate-600 flex-wrap font-['Inter']">Did you recieve a copy of the well permit?</h3>
                            <div className="self-stretch justify-start gap-[25px] inline-flex items-center">
                                <div className="items-center gap-2 flex">
                                    <input 
                                        type="radio" 
                                        id="copyOfWellPermitYes"
                                        value="yes" 
                                        checked={formData.copyOfWellPermit === 'yes'} 
                                        onChange={(e) => handleWaterRightsChange('copyOfWellPermit', e.target.value)}
                                    />
                                    <label className="text-black font-['Inter']">Yes</label>
                                </div>
                                <div className="items-center gap-2 flex">
                                    <input 
                                        type="radio" 
                                        id="copyOfWellPermitNo"
                                        value="no" 
                                        checked={formData.copyOfWellPermit === 'no'} 
                                        onChange={(e) => handleWaterRightsChange('copyOfWellPermit', e.target.value)}
                                    />
                                    <label className="text-black font-['Inter']">No</label>
                                </div>
                            </div>
                        </div>
                    </div>

                        <div className="flex flex-col justify-start mb-6">
                            <div className="items-center gap-2 mb-2 flex">
                                <input 
                                    type="checkbox" 
                                    id="wellRights"
                                    value={formData.wellRigths} 
                                    checked={formData.wellRights === 'yes'}
                                    onChange={(e) => handleWaterRightsChange('wellRights', e.target.checked ? 'yes' : 'no')}
                                />
                                <label className="text-[14px] md:text-sm font-[Inter] text-slate-600">Are There Well Rights?</label>
                            </div>
                                <span className="w-full text-slate-600 text-sm">List well permit number if you know of one.</span>
                                <input 
                                    type="text" 
                                    name="wellPermitNumber"
                                    value={formData.wellPermitNumber} 
                                    className="w-full border border-slate-300 rounded-md py-1 px-3 h-[30px]" 
                                    onChange={handleWaterRightsTextChange}
                                />
                        </div>

                        <div className="w-full flex flex-col flex-wrap items-center mb-6">
                            <h3 className="w-full text-[14px] md:text-sm font-[Inter] text-slate-600">Did you receive a copy of Seller's Property Disclosure or Source of Water Addendum?</h3>
                        <div className="self-stretch justify-start gap-[25px] inline-flex items-center">
                            <div className="items-center gap-2 flex">
                                <input 
                                    type="radio" 
                                    id="waterAddendumCertificateYes"
                                    value="yes" 
                                    checked={formData.waterAddendumCertificate === 'yes'} 
                                    onChange={(e) => handleWaterRightsReviewChange('waterAddendumCertificate', e.target.value)}
                                />
                                <label className="text-black font-['Inter']">Yes</label>
                            </div>
                            <div className="items-center gap-2 flex">
                                <input 
                                    type="radio" 
                                    id="waterAddendumCertificateNo"
                                    value="no" 
                                    checked={formData.waterAddendumCertificate === 'no'} 
                                    onChange={(e) => handleWaterRightsReviewChange('waterAddendumCertificate', e.target.value)}
                                />
                                <label className="text-black font-['Inter']">No</label>
                            </div>
                        </div>
                        </div>

                        <div className="flex flex-col justify-start mb-6">
                            <div className="items-center gap-2 mb-2 flex">
                                <input 
                                    type="checkbox" 
                                    id="waterStockCertificateYes"
                                    value="yes" 
                                    checked={formData.waterStockCertificate === 'yes'}
                                    onChange={(e) => handleWaterRightsChange('waterStockCertificate', e.target.checked ? 'yes' : 'no')}
                                />
                                <label className="text-[14px] md:text-sm font-[Inter] text-slate-600">Water Stock Certificates</label>
                            </div>
                                <span className="w-full text-slate-600 text-sm">List Water Stock Certificates to be transferred at closing</span>
                                <textarea 
                                    name="waterStockCertificateDescription"
                                    onChange={handleWaterRightsTextChange}
                                    value={formData.waterStockCertificateDescription}
                                    className="w-full bg-slate-100 p-1.5" 
                                />
                        </div>
                    </div>
                    )}
                </div>
            </div>
        </div>
        </div>

        <div className="flex flex-col gap-2 w-full mb-8 text-left">
        </div>

            {formData.additionalWaterRights === 'yes' && (            
            <div className="px-2 py-2 justify-start flex-col items-center w-full">
            </div>
            )}
            </div>

            <div className="flex w-full justify-center gap-10 md:gap-36 mt-2 bg-white">
                <button className="w-[100px] md:w-[150px] justify-center text-center text-shi-green bg-white border-white text-[16px] md:text-lg font-normal font-['Inter'] p-1.5 rounded-md border hover:bg-slate-200 items-center md:items-start flex" onClick={handleBack}>
                    Back
                </button>
                <button className="w-[100px] md:w-[150px] justify-center items-center text-center text-white bg-shi-green text-[16px] md:text-lg font-normal font-['Inter'] p-1.5 rounded-md border hover:bg-green-950 md:items-start flex" onClick={onNext}>
                    Next
                </button>
            </div>
        </MainCard>
        {isAssistVisible && (
                <div className="w-full md:w-auto absolute bottom-1 md:right-4">
                    <ShishitoAssist onClose={toggleAssist}/>
                </div>
            )}
        <InfoModal isOpen={isModalOpen} onClose={handleModalClose}>
                <h1 className="font-inter text-shi-green font-semibold text-xl">Start Here: Are you purchasing by yourself or with someone else?</h1>
                 <span>If you are purchasing by yourself, then there's nothing to do here. Select "No".</span>
        </InfoModal>
        </div>
    )
}

export default Miscellaneous; 